import React, { useState } from 'react';
import Modal from '../Modal';
import Alert from '../Alert';
import Button from '../Button/Button';
import ButtonRow from '../Button/ButtonRow';
import Icon from '../Icons/Icon';
import Title from '../Formatting/Customer/Title';
import TimeAgo from '../Formatting/TimeAgo';
import { useDialogues } from '../Dialogues';
import { useFailedWrites } from '../../Data/Local/Customer/hooks';
import outbox, { OP } from '../../Data/Local/Customer/outbox';
import MarkAsDone from '../../Pages/Customer/MarkAsDone';

/**
 * A failed row is nearly always a 409, and a 409 off these endpoints is a question
 * ("mark it done again, and deduct the cost again?") rather than a fault, so
 * retrying is not the answer to it. Until the user answers, the row blocks
 * everything queued behind it for that customer.
 */
const FailedWrites = ({isOpen, onClose}) => {
    const {failedWrites, retryWrite, sendAnyway, discardWrite, retryAll} = useFailedWrites(isOpen);
    const [reediting, setReediting] = useState(null);
    // A conflicted row needs an answer, not a retry, so a sweep would skip it.
    const retriable = failedWrites.filter(row => !row.conflict);

    return <Modal title="Changes that couldn't be sent" isOpen={isOpen} setIsOpen={onClose} dismissible>
        <div className="prose prose-sm mb-4">
            <p>
                These changes are still on this device. Usually the details changed
                elsewhere while you were offline — send them again, edit and resend,
                or discard them.
            </p>
        </div>

        {!failedWrites.length && <Alert theme="success"
            icon="Check"
            title="Nothing waiting"
            message="Every change on this device has been sent."
        />}

        <div className="flex flex-col gap-2">
            {failedWrites.map(row => <FailedWrite key={row.id}
                {...{row, retryWrite, sendAnyway, discardWrite}}
                onReedit={() => setReediting(row)}
            />)}
        </div>

        {retriable.length > 1 && <ButtonRow>
            <Button theme="secondary"
                label="Try all again"
                icon="ArrowRotateLeft"
                onClick={retryAll}
                onClickLoading
                availableOffline
            />
        </ButtonRow>}

        <Reedit row={reediting} onClose={() => setReediting(null)} />
    </Modal>
}

const summarise = (row) => {
    switch (row.op) {
        case OP.MARK_JOB_DONE:
            return 'Mark a job as done';
        case OP.SCHEDULE_JOB:
            return 'Schedule a job';
        case OP.UPDATE_CUSTOMER:
            return 'Update customer details';
        default:
            return 'Change';
    }
}

const FailedWrite = ({row, retryWrite, sendAnyway, discardWrite, onReedit}) => {
    const {confirm} = useDialogues();

    // The same confirmation the dialogue would have shown at the time, asked here
    // instead, in the server's own words rather than ours.
    const send = () => confirm(
        {
            theme: 'warning',
            title: row.conflict.title,
            message: row.conflict.message,
            confirmLabel: 'Send anyway',
        },
        () => sendAnyway(row.id),
    );

    const discard = () => confirm(
        {
            theme: 'danger',
            title: 'Discard this change?',
            message: 'It won\'t be sent, and this device will go back to what the server has.',
            confirmLabel: 'Discard',
        },
        () => discardWrite(row.id),
    );

    return <div className="rounded-xl border p-3 flex flex-col gap-2 text-sm">
        <div className="flex flex-row items-start justify-between gap-2">
            <div>
                <strong>{summarise(row)}</strong>
                {row.customer && <><br /><Title customer={row.customer} /></>}
                {row.job && <><br /><span className="text-muted">
                    {row.job.title ?? '#' + row.job.shortId}
                </span></>}
            </div>
            <span className="text-muted text-xs whitespace-nowrap">
                <TimeAgo dateTime={row.createdAt} />
            </span>
        </div>

        {row.conflict ? <p className="text-red-900 fill-red-900 flex flex-row items-start gap-2">
            <Icon icon="TriangleExclamation" />
            <span><strong>{row.conflict.title}.</strong> {row.conflict.message}</span>
        </p> : row.error && <p className="text-red-900 fill-red-900 flex flex-row items-start gap-2">
            <Icon icon="TriangleExclamation" />
            {row.error}
        </p>}

        <ButtonRow>
            <Button theme="lightDanger"
                label="Discard"
                icon="Trash"
                onClick={discard}
                availableOffline
                zeroMargin
            />
            {row.op === OP.MARK_JOB_DONE && !!row.job && <Button theme="light"
                label="Edit"
                icon="Pencil"
                onClick={onReedit}
                availableOffline
                zeroMargin
            />}
            {row.conflict ? <Button theme="primary"
                label="Send anyway"
                icon="PaperPlane"
                onClick={send}
                availableOffline
                zeroMargin
            /> : <Button theme="primary"
                label="Try again"
                icon="ArrowRotateLeft"
                onClick={() => retryWrite(row.id)}
                onClickLoading
                availableOffline
                zeroMargin
            />}
        </ButtonRow>
    </div>
}

// Submitting queues a fresh mark-done under the same key, replacing this failed row
// ({@see outboxKey}) and unblocking the queue behind it.
const Reedit = ({row, onClose}) => {
    if (!row) {
        return null;
    }

    return <MarkAsDone
        job={row.job}
        queued={row.payload?.data}
        markJobDone={(jobId, data) => outbox.markJobDone(row.entityId, jobId, data)}
        onClose={onClose}
        then={onClose}
    />
}

export default FailedWrites;
